// Usage:
// import EducationLoader from "./EducationLoader";
// ...
// {isLoading && <EducationLoader message="Loading lessons..." />} 
//
// Props (all optional):
// - message: string      → Helper text under the loader
// - size: number         → Icon/spinner size in px (default 56)
// - variant: 'icon'|'spinner' → Loader style (default 'icon')
// - fullscreen: boolean  → If true, fills and centers in the viewport

import * as React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import Paper from "@mui/material/Paper";
import { keyframes } from "@mui/system";
import SchoolIcon from "@mui/icons-material/School";

const wobble = keyframes`
  0%   { transform: rotate(0deg) translateY(0); filter: drop-shadow(0 0 0 rgba(25,118,210,.0)); }
  25%  { transform: rotate(8deg) translateY(-2px); filter: drop-shadow(0 0 6px rgba(25,118,210,.35)); }
  50%  { transform: rotate(0deg) translateY(0); filter: drop-shadow(0 0 0 rgba(25,118,210,.0)); }
  75%  { transform: rotate(-8deg) translateY(-2px); filter: drop-shadow(0 0 6px rgba(25,118,210,.35)); }
  100% { transform: rotate(0deg) translateY(0); filter: drop-shadow(0 0 0 rgba(25,118,210,.0)); }
`;

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

export default function EducationLoader({
  message = "Loading...",
  size = 56,
  variant = "icon",
  fullscreen = false,
}) {
  const Container = fullscreen ? Paper : Box;

  return (
    <Container
      elevation={fullscreen ? 0 : 0}
      sx={{
        position: fullscreen ? "fixed" : "relative",
        inset: fullscreen ? 0 : "unset",
        display: "grid",
        placeItems: "center",
        minHeight: fullscreen ? "100dvh" : "auto",
        background: fullscreen ? (theme => theme.palette.mode === 'dark' ? "linear-gradient(180deg, rgba(2,0,36,0) 0%, rgba(2,0,36,0.15) 100%)" : "linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(0,0,0,0.04) 100%)") : "transparent",
        p: fullscreen ? 2 : 0,
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
        {variant === "icon" ? (
          <Box
            aria-label="loading"
            role="img"
            sx={{
              position: "relative",
              width: size + 12,
              height: size + 12,
              display: "grid",
              placeItems: "center",
              "&:before": {
                content: '""',
                position: "absolute",
                inset: 0,
                borderRadius: "999px",
                border: theme => `3px dashed ${theme.palette.divider}`,
                animation: `${spin} 4.5s linear infinite`,
                opacity: 0.6,
              },
            }}
          >
            <SchoolIcon
              sx={{
                fontSize: size,
                color: theme => theme.palette.primary.main,
                animation: `${wobble} 1.4s ease-in-out infinite`,
              }}
            />
          </Box>
        ) : (
          <Box sx={{ position: "relative", display: "grid", placeItems: "center" }}>
            <CircularProgress size={size + 8} thickness={4} />
            <SchoolIcon
              sx={{
                position: "absolute",
                fontSize: size * 0.6,
                color: theme => theme.palette.primary.main,
              }}
            />
          </Box>
        )}

        {message && (
          <Typography
            variant="body2"
            sx={{
              mt: 1,
              opacity: 0.75,
              userSelect: "none",
              textAlign: "center",
              maxWidth: 420,
            }}
          >
            {message}
          </Typography>
        )}
      </Box>
    </Container>
  );
}
